import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { matchSchools, medicalSchools } from '../data/schools';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const C = {
  cream:     '#f8fafc',
  creamDark: '#f1f5f9',
  border:    '#e5e7eb',
  green:     '#2563eb',
  navy:      '#6366f1',
  red:       '#ef4444',
  textDark:  '#0f172a',
  textMid:   '#374151',
  textMuted: '#64748b',
};

const CAT = {
  safety:   { label: 'Target',    color: '#2563eb', needMin: 5, needMax: 7  },
  match:    { label: 'Match',     color: '#6366f1', needMin: 8, needMax: 10 },
  reach:    { label: 'Reach',     color: '#f59e0b', needMin: 5, needMax: 7  },
  longshot: { label: 'Long Shot', color: '#64748b', needMin: 0, needMax: 0  },
};

function getAdvisory(counts) {
  const ns = counts.safety || 0;
  const nm = counts.match  || 0;
  const nr = counts.reach  || 0;
  if (!ns && !nm && !nr) return 'Save schools from your match results to start building a balanced list of 20-25 schools.';
  const ts = Math.max(0, 5 - ns);
  const tm = Math.max(0, 8 - nm);
  const tr = Math.max(0, 5 - nr);
  if (ts > 0) return `Add ${ts} more Target school${ts !== 1 ? 's' : ''} -- schools where your stats are above most admitted students.`;
  if (tm > 0) return `Add ${tm} more Match school${tm !== 1 ? 's' : ''} -- schools where you are a realistic, competitive candidate.`;
  if (tr > 0) return `Add ${tr} more Reach school${tr !== 1 ? 's' : ''} -- aspirational schools where even strong applicants face real competition.`;
  return 'Your list looks well-balanced. A spread of 20-25 schools across Target, Match, and Reach gives you the best odds.';
}

function StatCell({ value, label }) {
  return (
    <div style={{ textAlign: 'right', minWidth: 56 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.textDark }}>{value}</div>
      <div style={{ fontSize: 9, color: C.textMuted }}>{label}</div>
    </div>
  );
}

function SavedRow({ school, onRemove, onNavigate, isMobile }) {
  return (
    <div
      onClick={onNavigate}
      style={{ background: C.creamDark, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'border-color .15s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = CAT[school.category]?.color || C.green; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.textDark, lineHeight: 1.3 }}>{school.name}</div>
        <div style={{ fontSize: 10, color: C.textMuted, marginTop: 1 }}>{school.city}, {school.state}</div>
      </div>
      {!isMobile && <StatCell value={school.mcat?.median ?? '--'} label="Median MCAT" />}
      {!isMobile && <StatCell value={school.gpa?.median != null ? school.gpa.median.toFixed(2) : '--'} label="Median GPA" />}
      {!isMobile && (school.mcatPercentile != null
        ? <StatCell value={`p${school.mcatPercentile}`} label="MCAT pct" />
        : <div style={{ minWidth: 56 }} />)}
      <button
        onClick={e => { e.stopPropagation(); onRemove(school.id); }}
        style={{ width: 28, height: 28, borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.cream, color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all .15s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = C.red; e.currentTarget.style.color = C.red; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}>
        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  );
}

function SuggestedRow({ school, onAdd, isMobile }) {
  return (
    <div style={{ background: C.cream, border: `0.5px dashed ${C.border}`, borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.textMid, lineHeight: 1.3 }}>{school.name}</div>
        <div style={{ fontSize: 10, color: C.textMuted, marginTop: 1 }}>{school.city}, {school.state}</div>
      </div>
      {!isMobile && <StatCell value={school.mcat?.median ?? '--'} label="Median MCAT" />}
      {!isMobile && <StatCell value={school.gpa?.median != null ? school.gpa.median.toFixed(2) : '--'} label="Median GPA" />}
      {!isMobile && (school.mcatPercentile != null
        ? <StatCell value={`p${school.mcatPercentile}`} label="MCAT pct" />
        : <div style={{ minWidth: 56 }} />)}
      <button
        onClick={() => onAdd(school)}
        style={{ width: 28, height: 28, borderRadius: 6, border: `0.5px solid ${C.green}`, background: C.cream, color: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
    </div>
  );
}

function AllMatchesRow({ school, isSaved, onAdd, onRemove, onNavigate, isMobile }) {
  const meta = CAT[school.category] || CAT.longshot;
  return (
    <div
      onClick={onNavigate}
      style={{ background: C.creamDark, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'border-color .15s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = meta.color; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}>
      <div style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: meta.color, color: '#fff', flexShrink: 0, letterSpacing: '.04em' }}>
        {meta.label.toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.textDark, lineHeight: 1.3 }}>{school.name}</div>
        <div style={{ fontSize: 10, color: C.textMuted, marginTop: 1 }}>{school.city}, {school.state}</div>
      </div>
      {!isMobile && <StatCell value={school.mcat?.median ?? '--'} label="MCAT" />}
      {!isMobile && <StatCell value={school.gpa?.median != null ? school.gpa.median.toFixed(2) : '--'} label="GPA" />}
      {!isMobile && (school.mcatPercentile != null
        ? <StatCell value={`p${school.mcatPercentile}`} label="MCAT pct" />
        : <div style={{ minWidth: 56 }} />)}
      <button
        onClick={e => { e.stopPropagation(); isSaved ? onRemove(school.id) : onAdd(school); }}
        style={{ padding: '5px 12px', borderRadius: 6, border: `0.5px solid ${isSaved ? C.border : C.green}`, background: isSaved ? C.creamDark : C.green, color: isSaved ? C.textMid : '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, transition: 'all .15s' }}>
        {isSaved ? 'Remove' : 'Save'}
      </button>
    </div>
  );
}

function CategoryGroup({ category, schools, suggested, onRemove, onAdd, navigate, isMobile }) {
  const meta = CAT[category];
  if (!meta || (schools.length === 0 && suggested.length === 0)) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Category header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 9, fontWeight: 700, padding: '3px 10px', borderRadius: 4, background: meta.color, color: '#fff', letterSpacing: '.06em' }}>
          {meta.label.toUpperCase()}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.textDark }}>{schools.length} saved</div>
        {meta.needMin > 0 && (
          <div style={{ fontSize: 11, color: C.textMuted }}>Recommended: {meta.needMin}-{meta.needMax}</div>
        )}
      </div>

      {/* Saved schools */}
      {schools.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: suggested.length > 0 ? 12 : 0 }}>
          {schools.map(s => (
            <SavedRow key={s.id} school={s} onRemove={onRemove} onNavigate={() => navigate(`/school/${s.id}`)} isMobile={isMobile} />
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic', marginBottom: suggested.length > 0 ? 10 : 0 }}>
          No {meta.label.toLowerCase()} schools saved yet.
        </div>
      )}

      {/* Suggested additions */}
      {suggested.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>
            Suggested additions
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {suggested.slice(0, 3).map(s => (
              <SuggestedRow key={s.id} school={s} onAdd={onAdd} isMobile={isMobile} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BuildMyList({ session }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [view, setView] = useState('list');
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) { setLoading(false); return; }
    supabase
      .from('saved_schools')
      .select('school_id')
      .eq('user_id', session.user.id)
      .then(({ data, error }) => {
        if (!error && data) setSavedIds(new Set(data.map(r => r.school_id)));
        setLoading(false);
      });
  }, [session]);

  const { stats, filters, hasStats } = useMemo(() => {
    try {
      const uid = session?.user?.id || 'guest';
      const s = JSON.parse(localStorage.getItem('previsr_stats_' + uid) || '{}');
      const f = JSON.parse(localStorage.getItem('previsr_filters_' + uid) || '{}');
      return {
        stats: s,
        filters: {
          degreeTypes: f.degreeTypes?.length ? f.degreeTypes : ['MD'],
          regions:     f.regions?.length     ? f.regions     : ['US'],
        },
        hasStats: !!(s.mcat && s.gpa && s.state),
      };
    } catch (e) {
      return { stats: {}, filters: { degreeTypes: ['MD'], regions: ['US'] }, hasStats: false };
    }
  }, [session]);

  const matchResults = useMemo(() => {
    if (!hasStats) return [];
    return matchSchools({
      mcat:        +stats.mcat,
      gpa:         +stats.gpa,
      sciGpa:      stats.sciGpa ? +stats.sciGpa : null,
      state:       stats.state,
      degreeTypes: filters.degreeTypes,
      regions:     filters.regions,
    }).sort((a, b) => {
      const order = { safety: 0, match: 1, reach: 2, longshot: 3 };
      const catDiff = (order[a.category] || 0) - (order[b.category] || 0);
      return catDiff !== 0 ? catDiff : (b.finalScore || 0) - (a.finalScore || 0);
    });
  }, [hasStats, stats, filters]);

  const matchById = useMemo(() => {
    const m = new Map();
    matchResults.forEach(r => m.set(r.id, r));
    return m;
  }, [matchResults]);

  // Saved schools enriched with matchSchools data (category from algorithm, not DB)
  const savedResults = useMemo(() => {
    const results = [];
    savedIds.forEach(id => {
      const match = matchById.get(id);
      if (match) {
        results.push(match);
      } else {
        const school = medicalSchools.find(s => s.id === id);
        if (school) results.push({ ...school, category: 'unmatched', finalScore: 0, mcatPercentile: null, gpaPercentile: null });
      }
    });
    return results;
  }, [savedIds, matchById]);

  const counts = useMemo(() => {
    const c = { safety: 0, match: 0, reach: 0, longshot: 0 };
    savedResults.forEach(s => { if (c[s.category] !== undefined) c[s.category]++; });
    return c;
  }, [savedResults]);

  const savedByCategory = useMemo(() => {
    const g = { safety: [], match: [], reach: [], longshot: [] };
    savedResults.forEach(s => { if (g[s.category]) g[s.category].push(s); });
    return g;
  }, [savedResults]);

  const suggestedByCategory = useMemo(() => {
    const g = { safety: [], match: [], reach: [], longshot: [] };
    matchResults.forEach(s => { if (!savedIds.has(s.id) && g[s.category]) g[s.category].push(s); });
    return g;
  }, [matchResults, savedIds]);

  const barTotal = counts.safety + counts.match + counts.reach;

  async function addSchool(school) {
    setSavedIds(prev => new Set([...prev, school.id]));
    const { error } = await supabase.from('saved_schools').insert({
      user_id:     session.user.id,
      school_id:   school.id,
      school_name: school.name,
      category:    school.category,
    });
    if (error) setSavedIds(prev => { const n = new Set(prev); n.delete(school.id); return n; });
  }

  async function removeSchool(schoolId) {
    setSavedIds(prev => { const n = new Set(prev); n.delete(schoolId); return n; });
    const { error } = await supabase.from('saved_schools').delete()
      .eq('user_id', session.user.id)
      .eq('school_id', schoolId);
    if (error) setSavedIds(prev => new Set([...prev, schoolId]));
  }

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.cream }}>
      <div style={{ width: 28, height: 28, border: `3px solid ${C.border}`, borderTopColor: C.green, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', background: C.cream }}>

      {/* Topbar */}
      <div style={{ background: C.creamDark, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '14px 16px' : '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.textDark, letterSpacing: '-0.5px' }}>Build My List</div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 1 }}>
            {savedIds.size} of 20-25 recommended schools saved
          </div>
        </div>
        <div style={{ display: 'flex', background: C.cream, border: `0.5px solid ${C.border}`, borderRadius: 8, padding: 3, gap: 2 }}>
          {[['list', 'My list'], ['all', 'All matches']].map(([key, label]) => (
            <button key={key} onClick={() => setView(key)}
              style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: view === key ? C.green : 'transparent', color: view === key ? '#fff' : C.textMid, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .15s' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, padding: isMobile ? '16px' : '20px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Balance bar — only when we have categorized saved schools */}
        {hasStats && savedIds.size > 0 && (
          <div style={{ background: C.creamDark, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', color: C.textMuted, marginBottom: 10 }}>List balance</div>

            {/* Stacked bar */}
            <div style={{ height: 12, borderRadius: 6, overflow: 'hidden', display: 'flex', background: C.border, marginBottom: 12 }}>
              {barTotal > 0 ? (
                <>
                  {counts.safety > 0 && <div style={{ width: `${(counts.safety / barTotal) * 100}%`, background: C.green,  height: '100%' }} />}
                  {counts.match  > 0 && <div style={{ width: `${(counts.match  / barTotal) * 100}%`, background: C.navy,   height: '100%' }} />}
                  {counts.reach  > 0 && <div style={{ width: `${(counts.reach  / barTotal) * 100}%`, background: C.red,    height: '100%' }} />}
                </>
              ) : null}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
              {[['safety', 'Target', '5-7'], ['match', 'Match', '8-10'], ['reach', 'Reach', '5-7']].map(([key, label, range]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: CAT[key].color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: C.textMid }}>
                    <span style={{ fontWeight: 700, color: C.textDark }}>{counts[key]}</span>
                    {' '}{label}
                    <span style={{ color: C.textMuted }}> (need {range})</span>
                  </span>
                </div>
              ))}
            </div>

            {/* Advisory nudge */}
            <div style={{ background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1e40af', lineHeight: 1.6 }}>
              {getAdvisory(counts)}
            </div>
          </div>
        )}

        {/* ── My list view ────────────────────────────────────────────── */}
        {view === 'list' && (
          <>
            {!hasStats && savedIds.size === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '60px 0', textAlign: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.textDark }}>No schools saved yet</div>
                <div style={{ fontSize: 13, color: C.textMuted, maxWidth: 360 }}>Enter your stats on the Dashboard, run the matcher, and save schools to build your list.</div>
                <button onClick={() => navigate('/dashboard')} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                  Go to Dashboard
                </button>
              </div>
            )}

            {!hasStats && savedIds.size > 0 && (
              <>
                <div style={{ background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#1e40af' }}>
                  Enter your stats on the Dashboard to see how each school categorizes for you.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {savedResults.map(s => (
                    <SavedRow key={s.id} school={s} onRemove={removeSchool} onNavigate={() => navigate(`/school/${s.id}`)} isMobile={isMobile} />
                  ))}
                </div>
              </>
            )}

            {hasStats && (
              <>
                {['safety', 'match', 'reach', 'longshot'].map(cat => (
                  <CategoryGroup
                    key={cat}
                    category={cat}
                    schools={savedByCategory[cat] || []}
                    suggested={cat !== 'longshot' ? (suggestedByCategory[cat] || []) : []}
                    onRemove={removeSchool}
                    onAdd={addSchool}
                    navigate={navigate}
                    isMobile={isMobile}
                  />
                ))}

                {savedIds.size === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark, marginBottom: 6 }}>Your list is empty</div>
                    <div style={{ fontSize: 12, color: C.textMuted }}>Use the suggested additions above, or switch to All Matches to browse and save schools.</div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── All matches view ─────────────────────────────────────────── */}
        {view === 'all' && (
          <>
            {!hasStats ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '60px 0', textAlign: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.textDark }}>No match results yet</div>
                <div style={{ fontSize: 13, color: C.textMuted, maxWidth: 360 }}>Enter your MCAT, GPA, and state on the Dashboard to see all matched schools here.</div>
                <button onClick={() => navigate('/dashboard')} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                  Go to Dashboard
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600 }}>{matchResults.length} schools matched</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {matchResults.map(school => (
                    <AllMatchesRow
                      key={school.id}
                      school={school}
                      isSaved={savedIds.has(school.id)}
                      onAdd={addSchool}
                      onRemove={removeSchool}
                      onNavigate={() => navigate(`/school/${school.id}`)}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}
